import React, { FC } from "react";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { FormattedNumber } from "@I18n/Components/FormattedNumber/FormattedNumber";
import { IServiceOffer } from "@modules/ServiceAssignment/services/serviceOffers";
import messages from "./messages";
import serviceRequestMessages from "@modules/ServiceRequest/Components/messages";

interface IProps {
  serviceOffer: IServiceOffer;
  serviceProviderName: string | undefined;
  isVisitOffer: boolean;
  unitNode: React.ReactNode;
  serviceFeePercentage: number;
  serviceFeeAmountCents: number;
  totalExpensesCents: number;
  expensesFeesCents: number;
  variant: "standard" | "subscription";
}

export const FormulaFeeBreakdown: FC<IProps> = ({
  serviceOffer,
  serviceProviderName,
  isVisitOffer,
  unitNode,
  serviceFeePercentage,
  serviceFeeAmountCents,
  totalExpensesCents,
  expensesFeesCents,
  variant,
}) => {
  const isSubscription = variant === "subscription";
  const feeColor = isSubscription ? "green.500" : "gray.800";

  return (
    <Box borderTop="1px solid" borderColor="gray.200" pt="2">
      {/* Service row — identical for both variants */}
      <Flex justifyContent="space-between">
        <Text fontSize="sm" color="gray.600">
          <FormattedMessage
            {...messages.serviceRow}
            values={{ serviceProviderName }}
          />
        </Text>
        <Text fontSize="sm" fontWeight="bold" color="gray.800" flexShrink={0}>
          {isVisitOffer ? (
            <FormattedMessage
              {...serviceRequestMessages["serviceRequest.visitOfferDetail.status"]}
            />
          ) : unitNode ? (
            <>
              <FormattedNumber
                value={serviceOffer.rateCents / 100}
                style="currency"
                currency={serviceOffer.rateCurrency}
                minimumFractionDigits={0}
              />
              {unitNode}
              {totalExpensesCents > 0 && (
                <>
                  {" + "}
                  <FormattedNumber
                    value={totalExpensesCents / 100}
                    style="currency"
                    currency={serviceOffer.rateCurrency}
                    minimumFractionDigits={0}
                  />
                </>
              )}
            </>
          ) : (
            <FormattedNumber
              value={(serviceOffer.rateCents + totalExpensesCents) / 100}
              style="currency"
              currency={serviceOffer.rateCurrency}
              minimumFractionDigits={0}
            />
          )}
        </Text>
      </Flex>

      {/* Fee row — subscription always shows €0 in green; standard shows actual fee */}
      <Flex justifyContent="space-between">
        <Text fontSize="sm" color={feeColor}>
          <FormattedMessage {...messages.serviceFeeRow} />
        </Text>
        <Text
          fontSize="sm"
          fontWeight="bold"
          color={feeColor}
          flexShrink={isSubscription ? undefined : 0}
        >
          {isSubscription ? (
            <FormattedNumber
              value={0}
              style="currency"
              currency={serviceOffer.rateCurrency}
              minimumFractionDigits={0}
            />
          ) : isVisitOffer ? (
            <FormattedMessage
              {...messages.serviceFeePercentage}
              values={{ percentage: serviceFeePercentage }}
            />
          ) : unitNode ? (
            <>
              <FormattedNumber
                value={serviceFeeAmountCents / 100}
                style="currency"
                currency={serviceOffer.rateCurrency}
                minimumFractionDigits={0}
              />
              {unitNode}
              {expensesFeesCents > 0 && (
                <>
                  {" + "}
                  <FormattedNumber
                    value={expensesFeesCents / 100}
                    style="currency"
                    currency={serviceOffer.rateCurrency}
                    minimumFractionDigits={0}
                  />
                </>
              )}
            </>
          ) : (
            <FormattedNumber
              value={(serviceFeeAmountCents + expensesFeesCents) / 100}
              style="currency"
              currency={serviceOffer.rateCurrency}
              minimumFractionDigits={0}
            />
          )}
        </Text>
      </Flex>
    </Box>
  );
};
